import { useState, forwardRef } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import HttpService from "../services/HttpService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CircularProgress,
  Container,
  Grid,
  Button,
  Snackbar,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Box,
} from "@mui/material";
import cardImg from "./../images/cards/card.png";
import Labels from "../labels/Labels";

import RequestCardsDialog from "../components/RequestCardsDialog";
import CancelRequestCardsDialog from "../components/CancelRequestCardsDialog";
import RenderOnRole from "../components/RenderOnRole";
import RenderIfNotOnRole from "../components/RenderIfNotOnRole";
import UserService from "../services/UserService";
import MuiAlert from "@mui/material/Alert";
import RefreshingIndicator from "../components/RefreshingIndicator";
import ButtonWithIcon from "../components/Button";
import AddCardIcon from "@mui/icons-material/AddCard";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ConfirmOrderNumberDialog from "../components/ConfirmOrderNumberDialog";
import BundleDetail from "./BundlesDetail";

export default function Bundles(props) {
  const APPROVE_BUNDLE_ROLE = "pc-approve-bundle";

  // Utility function to check if a string is a valid URL
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const queryClient = useQueryClient();

  const updateBundleMutation = useMutation(
    (updatedBundle) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/paycards/bundle`,
        updatedBundle
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bundles"] });
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "bundles.RequestVerifiedSuccess",
            "The request was verified succesfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const deleteBundleMutation = useMutation(
    (deleteBundleId) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/paycards/bundle/` + deleteBundleId
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bundles"] });
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "bundles.OrderDeletedSuccess",
            "The order was deleted succesfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const downloadBundleMutation = useMutation(
    (params) => {
      return HttpService.getAxiosClient().get(
        window.API_URL + `/paycards/bundle/export/` + params.id,
        { responseType: "blob" }
      );
    },
    {
      onSuccess: (response) => {
        const { headers, data } = response;
        const disposition = headers["content-disposition"];
        let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
        if (filename.toLowerCase().startsWith("utf-8''"))
          filename = decodeURIComponent(filename.replace("utf-8''", ""));
        else filename = filename.replace(/['"]/g, "");
        let url = window.URL.createObjectURL(data);
        let a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); // append the element to the dom
        a.click();
        a.remove(); // afterwards, remove the element
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "bundles.FileDownloadSuccess",
            "The file was download succesfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        if (err?.response?.status === 500) {
          setFeedback({
            open: true,
            type: "error",
            message: Labels.getLabel(
              "bundles.FileDownloadError",
              "There was an error trying to download the file"
            ),
          });
        } else {
          const msg = err?.response?.data ? err.response.data : err.message;
          setFeedback({
            open: true,
            type: "error",
            message: msg,
          });
        }
      },
    }
  );

  const onViewButtonClick = (e, params) => {
    setChosenBundle(params);
  };

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("bundles.column.id", "ID"),
      width: 1,
      hide: true,
    },
    {
      field: "idEmployer",
      headerName: Labels.getLabel("bundles.column.employer", "Employer"),
      width: 180,
      hide: !UserService.hasRole([APPROVE_BUNDLE_ROLE]),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: 12, mb: 0, color: "black" }}
              gutterBottom
            >
              {params.row.employerName}
            </Typography>
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.idEmployer}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: Labels.getLabel("bundles.column.description", "Description"),
      width: 350,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "left",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{ fontSize: 12, mb: 0, color: "black" }}
              gutterBottom
            >
              {params.row.description}
            </Typography>
            <Typography
              sx={{ fontSize: 10, mb: 0, color: "grey", fontWeight: "bold" }}
              gutterBottom
            >
              {params.row.cardType}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "embossPart1",
      headerName: Labels.getLabel("bundles.column.embossing", "Embossing"),
      width: 190,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "left",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{ fontSize: 12, mb: 0, color: "black" }}
              gutterBottom
            >
              {params.row.embossPart1} {params.row.embossPart2}
            </Typography>
            <Typography
              sx={{ fontSize: 10, mb: 0, color: "grey", fontWeight: "bold" }}
              gutterBottom
            >
              {params.row.embossPart3}
            </Typography>
          </div>
        );
      },
    },

    {
      field: "idLocation",
      headerName: Labels.getLabel("bundles.column.location", "Location"),
      width: 130,
      hide: UserService.hasRole([APPROVE_BUNDLE_ROLE]),
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.idLocation}
          </Typography>
        );
      },
    },
    {
      field: "quantity",
      headerName: Labels.getLabel("bundles.column.count", "Count"),
      headerAlign: "right",
      width: 100,
      align: "right",
    },
    {
      field: "totalAmount",
      headerName: Labels.getLabel("bundles.column.loadAmount", "Load Amount"),
      headerAlign: "right",
      width: 150,
      hide: !UserService.hasRole([APPROVE_BUNDLE_ROLE]),
      align: "right",
      valueFormatter: (params) => {
        const formatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        });
        return formatter.format(params.value);
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel("bundles.column.updateDate", "Update Date"),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("bundles.column.status", "Status"),
      width: 140,
      renderCell: (params) => {
        return (
          <Typography
            sx={{ fontSize: 12, mb: 0, fontWeight: "600" }}
            gutterBottom
          >
            {params.row.status}
          </Typography>
        );
      },
    },
    {
      field: "action",
      headerName: Labels.getLabel("bundles.column.action", "Action"),
      width: 240,
      hide: !UserService.hasRole([APPROVE_BUNDLE_ROLE]),
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div>
            <ButtonWithIcon
              onClick={onViewButtonClick}
              color="blue"
              tooltip={Labels.getLabel(
                "bundles.ViewTooltip",
                "Click to view more info of the request"
              )}
              icon="view"
              record={params.row}
            />
            <ButtonWithIcon
              onClick={onVerifyButtonClick}
              hidden={params.row.status !== "REQUESTED"}
              disabled={updateBundleMutation.isLoading || isFetching}
              color="green"
              tooltip={Labels.getLabel(
                "bundles.VerifyTooltip",
                "Click to verify and accept the request"
              )}
              icon="approve-card"
              record={params.row}
            />
            <ButtonWithIcon
              onClick={onCloseButtonClick}
              hidden={params.row.status !== "REQUESTED"}
              color="red"
              tooltip={Labels.getLabel(
                "bundles.CancelTooltip",
                "Click to cancel and close the request"
              )}
              icon="deny"
              record={params.row}
            />
            <RenderOnRole roles={["pc-program-super-admin"]}>
              <ButtonWithIcon
                onClick={onActivateButtonClick}
                disabled={updateBundleMutation.isLoading || isFetching}
                hidden={params.row.status !== "READY_TO_ACTIVATE"}
                color="green"
                tooltip={Labels.getLabel(
                  "bundles.ActivateTooltip",
                  "Click to activate the cards"
                )}
                icon="money"
                record={params.row}
              />
            </RenderOnRole>

            <ButtonWithIcon
              onClick={onDeleteButtonClick}
              hidden={params.row.status !== "VALIDATION_ERROR"}
              disabled={deleteBundleMutation.isLoading || isFetching}
              color="red"
              tooltip={Labels.getLabel(
                "bundles.DeleteTooltip",
                "Click to delete the request"
              )}
              icon="delete"
              record={params.row}
            />
            <RenderOnRole roles={["pc-program-super-admin"]}>
              <ButtonWithIcon
                onClick={onDownloadButtonClick}
                hidden={params.row.status !== "PENDING_BULK"}
                color="blue"
                tooltip={Labels.getLabel(
                  "bundles.DownloadTooltip",
                  "Click to download the CSV file from the order"
                )}
                icon="download"
                record={params.row}
              />
              <ButtonWithIcon
                onClick={onConfirmButtonClick}
                hidden={params.row.status !== "PENDING_BULK"}
                color="green"
                tooltip={Labels.getLabel(
                  "bundles.ConfirmationTooltip",
                  "Click to enter the confirmation number"
                )}
                icon="confirm"
                record={params.row}
              />
            </RenderOnRole>
          </div>
        );
      },
    },
    {
      field: "shipping",
      headerName: Labels.getLabel("bundles.column.shipping", "Shipping"),
      width: 100,
      renderCell: (params) => {
        return (
          <ButtonWithIcon
            onClick={onShippingButtonClick}
            hidden={
              !params.row.shippingInfo || !isValidUrl(params.row.shippingInfo)
            }
            //color="orange"
            tooltip={Labels.getLabel(
              "bundles.ShippingTooltip",
              "Click to view shipping information"
            )}
            icon="shipping"
            record={params.row}
          />
        );
      },
    },
    {
      field: "remark",
      headerName: Labels.getLabel("bundles.column.notes", "Notes"),
      width: 180,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.remark}>
            <span>{params.row.remark}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "createdBy",
      headerName: Labels.getLabel("bundles.column.createdBy", "Created By"),
      width: 180,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.createdBy}
          </Typography>
        );
      },
    },
  ];

  const onAddButtonClick = (e, params) => {
    setOpenNewDlg(true);
  };

  const onDownloadButtonClick = (e, params) => {
    downloadBundleMutation.mutate(params);
  };

  const onConfirmButtonClick = (e, params) => {
    setInfoToConfirm(params);
    setOpenConfirmDlg(true);
  };

  const onVerifyButtonClick = (e, params) => {
    const updateRequest = {
      id: params.id,
      process: "VERIFY",
    };
    updateBundleMutation.mutate(updateRequest);
  };

  const onActivateButtonClick = (e, params) => {
    const updateRequest = {
      id: params.id,
      process: "ACTIVATE",
    };
    updateBundleMutation.mutate(updateRequest);
  };

  const onDeleteButtonClick = (e, params) => {
    deleteBundleMutation.mutate(params.id);
  };

  const onCloseButtonClick = (e, params) => {
    setInfoToDiscard(params);
    setOpenDiscardDlg(true);
  };

  const onShippingButtonClick = (e, params) => {
    // Open URL in a new tab to bypass X-Frame-Options restrictions
    window.open(params.shippingInfo, "_blank", "noopener,noreferrer");
  };

  async function fetchBundles() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/paycards/bundle`
    );
    return response;
  }

  const { data, isLoading, isFetching, isError } = useQuery(
    ["bundles"],
    fetchBundles
  );

  const [openNewDlg, setOpenNewDlg] = useState(false);

  const [chosenBundle, setChosenBundle] = useState();

  const [openDiscardDlg, setOpenDiscardDlg] = useState(false);

  const [infoToDiscard, setInfoToDiscard] = useState({});

  const [infoToConfirm, setInfoToConfirm] = useState({});

  const [openConfirmDlg, setOpenConfirmDlg] = useState(false);

  return (
    <Container maxWidth="xlg">
      {chosenBundle ? (
        <Dialog
          open={chosenBundle !== undefined}
          onClose={() => setChosenBundle()}
          maxWidth="xl"
          fullWidth
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <VisibilityIcon sx={{ color: "white" }} />
              <Typography variant="h6" sx={{ color: "white" }}>
                {Labels.getLabel(
                  "bundles.ViewDialogTitle",
                  "View Card Order Request"
                )}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setChosenBundle()}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ paddingTop: 3 }}>
            <BundleDetail bundle={chosenBundle} />
          </DialogContent>
        </Dialog>
      ) : (
        ""
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "20px" }}>
              <h1>{Labels.getLabel("bundles.PageTitle", "Cards Requested")}</h1>
            </div>
            {isFetching ? <RefreshingIndicator /> : ""}
          </div>
          {isLoading ? (
            <CircularProgress color="inherit" />
          ) : !isError ? (
            <div style={{ height: 550, width: "100%" }}>
              <RenderOnRole roles={["pc-create-bundle"]}>
                <Button
                  style={{ margin: "10px" }}
                  onClick={(e) => onAddButtonClick(e)}
                  variant="contained"
                >
                  <AddCardIcon style={{ marginRight: "10px" }}></AddCardIcon>
                  {Labels.getLabel(
                    "bundles.RequestCardsButton",
                    "Request Cards"
                  )}
                </Button>
              </RenderOnRole>

              <DataGrid
                rows={data?.data}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
              <Snackbar
                open={feedback.open}
                autoHideDuration={6000}
                onClose={
                  feedback.onClose ? feedback.onClose : handleFeedbackClose
                }
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={
                    feedback.onClose ? feedback.onClose : handleFeedbackClose
                  }
                  severity={feedback.type}
                  sx={{ width: "100%" }}
                >
                  {feedback.message}
                </Alert>
              </Snackbar>
            </div>
          ) : (
            <h4>
              {Labels.getLabel(
                "bundles.ErrorMessage",
                "An error ocurred, please try again later..."
              )}
            </h4>
          )}
        </Grid>
      </Grid>
      {openNewDlg ? (
        <RequestCardsDialog
          action="Request"
          open={openNewDlg}
          setOpen={setOpenNewDlg}
          dialogTitle={Labels.getLabel(
            "bundles.RequestDialogTitle",
            "Request Cards"
          )}
          dialogContentText={Labels.getLabel(
            "bundles.RequestDialogText",
            "To request cards, input the information and click request button."
          )}
        />
      ) : (
        ""
      )}
      {openDiscardDlg ? (
        <CancelRequestCardsDialog
          action="DISCARD"
          open={openDiscardDlg}
          setOpen={setOpenDiscardDlg}
          dialogTitle={Labels.getLabel(
            "bundles.DiscardDialogTitle",
            "Discard Request"
          )}
          dialogContentText={Labels.getLabel(
            "bundles.DiscardDialogText",
            "To discard this request, type the reason and click DISCARD button."
          )}
          infoToDiscard={infoToDiscard}
        />
      ) : (
        ""
      )}
      {openConfirmDlg ? (
        <ConfirmOrderNumberDialog
          action="CONFIRM"
          open={openConfirmDlg}
          setOpen={setOpenConfirmDlg}
          dialogTitle={Labels.getLabel(
            "bundles.ConfirmDialogTitle",
            "Confirm Order"
          )}
          dialogContentText={Labels.getLabel(
            "bundles.ConfirmDialogText",
            "To confirm the order, type order confirmation number and click CONFIRM button."
          )}
          infoToConfirm={infoToConfirm}
        />
      ) : (
        ""
      )}
    </Container>
  );
}
